import { Text, VStack } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ScrollingInputDropdown } from '~/components/shared/Dropdown/ScrollingInputDropdown'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useGetPhoneNumbers } from '~/queries/phoneNumberQueries'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { formatPhoneNumber } from '~/utils/formatter'

import { useDebouncedCallback } from '@mangovoice/core'
import { PhoneNumber } from '@mangovoice/types'

import { OutboundPhoneSearchListItem } from './OutboundPhoneSearchListItem'

const SEARCH_DEBOUNCE_RATE_MS = 300

interface OutboundPhoneSearchInputProps {
  isInvalid?: boolean
  value?: string
  onValueChanged?: (value: PhoneNumber | null) => void
}

type PhoneLabelValue = {
  phone: PhoneNumber
}

const getPhoneLabelData = (phoneNumbers?: PhoneNumber[]) => {
  if (!phoneNumbers) {
    return []
  }

  return phoneNumbers?.map((phoneNumber) => {
    return {
      value: { phone: phoneNumber },
      content: (
        <MenuListItem>
          <OutboundPhoneSearchListItem phoneNumber={phoneNumber} />
        </MenuListItem>
      ),
    }
  })
}

const getInitialSearchValue = (value: string) => {
  if (value.includes(':')) {
    return value.split(':')[0]
  }
  return value
}

export const OutboundPhoneSearchInput = ({
  isInvalid,
  value = '',
  onValueChanged,
}: OutboundPhoneSearchInputProps) => {
  const { t } = useTranslation()
  const [isFocused, setIsInputFocused] = useState<boolean>(false)
  const [canSignalInvalid, setCanSignalInvalid] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState(
    getInitialSearchValue(value ?? ''),
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedSearch = useDebouncedCallback((_value: string) => {
    setInputValue(_value)
  }, SEARCH_DEBOUNCE_RATE_MS)
  const analytics = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const {
    results: phoneNumbers,
    isFetching: isPhoneListFetching,
    fetchNextPage,
  } = useGetPhoneNumbers({
    search: inputValue,
  })

  const handleFocus = (focused: boolean) => {
    setIsInputFocused(focused)
    if (!focused) {
      setCanSignalInvalid(true)
    }
  }

  const handleValueChanged = (value: string) => {
    if (!value) {
      onValueChanged?.(null)
    }
    debouncedSearch(value)
  }

  const handleSelected = (data: PhoneLabelValue) => {
    onValueChanged?.(data.phone)
    if (inputRef?.current) {
      inputRef.current.value = `${data.phone.name}: ${formatPhoneNumber(data.phone.number)}`
    }
    analytics.track(WebPhoneAnalyticsEvents.OutboundSearchInputExtensionClicked)
  }

  useEffect(() => {
    if (value !== inputRef?.current?.value) {
      setInputValue(getInitialSearchValue(value))
      if (inputRef?.current) {
        inputRef.current.value = value
      }
    }
  }, [value])
  const shouldBeOpen = isFocused && (phoneNumbers?.length ?? 0) > 0
  const isToInvalid = canSignalInvalid && isInvalid
  const isLoading = isPhoneListFetching && !phoneNumbers
  return (
    <VStack gap="0.375rem" align="stretch" width="100%">
      <ScrollingInputDropdown
        inputRef={inputRef}
        items={getPhoneLabelData(phoneNumbers)}
        isOpen={shouldBeOpen}
        defaultValue={value}
        onMenuClose={() => handleFocus(false)}
        onFocus={() => handleFocus(true)}
        onScrollEndReached={fetchNextPage}
        autoCloseOnBlur
        autoCloseOnSelected
        onSelected={handleSelected}
        onValueChanged={handleValueChanged}
        placeholder={
          value ?? t('outboundPhoneSearch.placeholder', 'Select Number')
        }
        isInvalid={isToInvalid}
        isLoading={isLoading}
      />
      {isToInvalid && isInvalid && (
        <Text color="red.solid">
          {t('contactSearchInput.invalidPhoneNumber', 'Invalid phone number')}
        </Text>
      )}
    </VStack>
  )
}
